import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';
import { useSelector } from 'react-redux';

import { UserHasAccessActions } from 'components/user-access';
import { Filter, FilterButton } from 'components/filtering';
import { FormTextInput } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import { DateToLocal } from 'components/dates';
import { Button } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { Tooltip } from 'components/tooltip';
import { AlertConfirm, NotificationInline, NotificationToaster } from 'components/notifications';
import { useDebounce } from 'hooks/useDebounce';
import { BulkImportMappingService } from 'services';

import { canManageGlobalMappings, importSources, importTypes } from './bulkImportTypes';
import { buildComparator } from './listingComparator';
import { buildFailureWarningText, splitSettledSources } from './sourceFanOut';
import { BulkImports } from './BulkImports';

const defaultArray = [];

const debounceTimeout = 1500;
const badRequestCode = 400;
const fetchRequestCount = 500;
const defaultPageSize = 25;
const generalLoadErrorMessage = "An error occurred while loading the saved mappings. Please try again.";
const generalDeleteErrorMessage = "Unable to delete the mapping, please try again.";

const scopeFilterName = "SCOPE";
const typeFilterName = "TYPE";

// A mapping is either personal to the caller or shared account-wide; the wire filter is the
// isGlobal flag, so the item ids carry the flag's string form.
const scopeFilterItems = [
    { id: "false", name: "Personal" },
    { id: "true", name: "Global" }
];

// Derived from the import-type registry so the filter options stay in step as non-legacy
// import types are added.
const typeFilterItems = importTypes
    .filter((type) => !type.isLegacy)
    .map((type) => ({ id: type.bulkImportTypeKey, name: type.typeName }));

const tableHeaders = [
    "Name",
    "Scope",
    "Import Type",
    "Created By",
    "Created",
    "Modified",
    ""
];

// The sortName identifies which row field the in-memory comparator orders by, and how to
// coerce its values for comparison (string or date).
const sortableHeaders = [
    { headerName: "Name", sortName: "mappingName" },
    { headerName: "Scope", sortName: "scopeName" },
    { headerName: "Import Type", sortName: "bulkImportTypeKey" },
    { headerName: "Created By", sortName: "createdBy" },
    { headerName: "Created", sortName: "createdDate" },
    { headerName: "Modified", sortName: "modifiedDate" }
];

const defaultSortHeader = sortableHeaders.find((header) => header.sortName === "modifiedDate");

const dateSortFields = ["createdDate", "modifiedDate"];

// Rows carry the raw type key; the Import Type column shows the registry's friendly name,
// falling back to the key itself for types the registry doesn't know.
function getImportTypeName(bulkImportTypeKey) {
    const importType = importTypes.find((type) => type.bulkImportTypeKey === bulkImportTypeKey);
    return importType?.typeName ?? bulkImportTypeKey;
}

// The Saved Mappings tab's listing body — the fan-out fetch, search, filters, per-row
// delete, and the in-memory sorted/paged table. The page shell (heading, New Import button,
// tabs) lives in BulkImports.
function SavedMappingsPanel() {

    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(defaultArray);

    // The full fetched set is held in state and sorted/sliced in memory; only the search
    // term and filters trigger a re-fetch.
    const [allMappings, setAllMappings] = useState(defaultArray);

    // Set when some sources returned rows and others failed, so the rendered list is partial.
    const [sourceFailureText, setSourceFailureText] = useState(null);

    const [sortedBy, setSortedBy] = useState(defaultSortHeader);
    const [sortedDir, setSortedDir] = useState("D");

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [mappingToDelete, setMappingToDelete] = useState(null);

    const inputSearchRef = useRef(null);

    const filterName = "bulkImportMappings";
    const pageFilters = [
        {
            name: scopeFilterName,
            displayName: "Scope",
            items: scopeFilterItems,
            singleSelect: true
        },
        {
            name: typeFilterName,
            displayName: "Import Type",
            items: typeFilterItems,
            singleSelect: true
        }
    ];

    const userActions = useSelector((state) => state.loggedInUser.actions);
    const userRole = useSelector((state) => state.loggedInUser.role);

    // Personal mappings are always deletable — the API only returns the caller's own.
    const canDeleteGlobal = canManageGlobalMappings(userRole);

    // Only fetch the mapping sources the user's claims allow; each source carries the claims
    // of the types it hosts, and holding any one grants access. A user with no accessible
    // source can still reach the page (to start a new import) but sees no mappings table.
    const accessibleSources = importSources.filter((source) => UserHasAccessActions(source.requiredClaims, userActions));
    const canViewMappings = accessibleSources.length > 0;

    const selectedScope = appliedFilters.find((f) => f.name === scopeFilterName)?.value ?? null;
    const selectedType = appliedFilters.find((f) => f.name === typeFilterName)?.value ?? null;

    function fetchMappings() {
        setIsLoading(true);

        const filters = [];
        if (selectedScope != null) {
            filters.push({ key: "isGlobal", value: selectedScope });
        }
        if (selectedType != null) {
            filters.push({ key: "bulkImportTypeKey", value: selectedType });
        }

        const searchRequest = {
            requestCount: fetchRequestCount,
            pageNumber: 1,
            sortField: "modifiedDate",
            sortDirection: "D",
            searchTerm: debouncedSearchTerm,
            filters: filters
        };

        // One request per accessible source; each row keeps its source so the delete can
        // route to the correct microservice. The scope label is stamped up front so the
        // Scope column can be sorted with the shared string comparator.
        const requests = accessibleSources.map((source) => {
            return BulkImportMappingService.searchMappings(source.sourceKey, searchRequest).then(
                (response) => {
                    return (response?.data ?? []).map((row) => {
                        return { ...row, sourceKey: source.sourceKey, scopeName: row.isGlobal ? "Global" : "Personal" };
                    });
                }
            );
        });

        // Settled rather than all-or-nothing: one unavailable microservice must not blank the
        // whole listing when the others answered.
        Promise.allSettled(requests).then((settledResults) => {
            const { rows, failures } = splitSettledSources(accessibleSources, settledResults);

            if (failures.length === accessibleSources.length) {
                // Nothing loaded, so there is no partial list to caption — the failure is
                // reported as a toast, driven off the first rejection.
                const firstFailure = failures[0].reason;
                if (firstFailure?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, firstFailure.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalLoadErrorMessage);
                }
                setSourceFailureText(null);
            } else if (failures.length > 0) {
                // A partial list is a quieter failure than an empty one, so the warning has to
                // persist alongside the rows rather than disappear like a toast.
                setSourceFailureText(buildFailureWarningText(failures, "mappings", userActions));
            } else {
                setSourceFailureText(null);
            }

            setAllMappings(rows);
            setPageNumber(1);
            setIsLoading(false);
        });
    }

    useEffect(() => {
        // Only fetch when the user can access at least one mapping source; searchMappings
        // would 403 otherwise, so skip the fetch and clear the loading state for them.
        if (canViewMappings) {
            fetchMappings();
        } else {
            setIsLoading(false);
        }
    }, [debouncedSearchTerm, selectedScope, selectedType]);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onToggleFilter() {
        setShowFilters((previousState) => {
            return !previousState;
        });
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    // Sorting and paging are client-side only — they re-order or re-slice the in-memory
    // array without re-fetching.
    function onTableSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onDeleteClick(row) {
        setMappingToDelete(row);
        setShowDeleteConfirm(true);
    }

    function onDeleteConfirm() {
        setShowDeleteConfirm(false);
        setIsLoading(true);

        BulkImportMappingService.deleteMapping(mappingToDelete.sourceKey, mappingToDelete.bulkImportMappingId).then(
            () => {
                NotificationToaster.show(Intent.SUCCESS, `Mapping '${mappingToDelete.mappingName}' has been successfully deleted.`);
                setMappingToDelete(null);
                fetchMappings();
            },
            (errorResponse) => {
                if (errorResponse?.status === badRequestCode) {
                    NotificationToaster.show(Intent.DANGER, errorResponse.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalDeleteErrorMessage);
                }
                setMappingToDelete(null);
                setIsLoading(false);
            }
        );
    }

    function onDeleteCancel() {
        setMappingToDelete(null);
        setShowDeleteConfirm(false);
    }

    const comparator = buildComparator({
        sortField: sortedBy.sortName,
        direction: sortedDir,
        dateSortFields: dateSortFields,
        tieBreakField: "mappingName"
    });
    const sortedMappings = [...allMappings].sort(comparator);

    const pageStart = (pageNumber - 1) * pageSize;
    const pageRows = sortedMappings.slice(pageStart, pageStart + pageSize);

    const tableRows = pageRows.map((row) => {
        const rowKey = `${row.sourceKey}-${row.bulkImportMappingId}`;

        return [
            row.mappingName,
            row.scopeName,
            getImportTypeName(row.bulkImportTypeKey),
            row.createdBy,
            <DateToLocal key={`created-${rowKey}`} format="DD MMM YYYY">{row.createdDate ?? "-"}</DateToLocal>,
            <DateToLocal key={`modified-${rowKey}`} format="DD MMM YYYY">{row.modifiedDate ?? "-"}</DateToLocal>,
            <div className="button-row-small" key={`actions-${rowKey}`}>
                <ShowHide
                    evaluator={!row.isGlobal || canDeleteGlobal}
                    show={(
                        <Tooltip content="Delete mapping">
                            <Button onClick={() => onDeleteClick(row)} icon="trash" iconOnly={true} text="Delete Mapping" large={false} minimal={true} id={`delete-mapping-${rowKey}`}></Button>
                        </Tooltip>
                    )}
                    hide={(
                        <Tooltip content="Only a Company Admin or Account Owner can delete a global mapping">
                            <Button onClick={() => { }} icon="trash" iconOnly={true} text="Delete Mapping" large={false} minimal={true} disabled id={`delete-mapping-${rowKey}`}></Button>
                        </Tooltip>
                    )}
                />
            </div>
        ];
    });

    // With no search or filter applied an empty result means the user has never saved a
    // mapping, so the empty state explains how to create one; with criteria applied it is
    // just an empty search result.
    const hasActiveCriteria = debouncedSearchTerm !== "" || selectedScope != null || selectedType != null;

    let noDataHeading = null;
    let noDataMessage = "No mappings found, please try a different search term or filter.";
    if (!hasActiveCriteria) {
        noDataHeading = "You haven't saved any mappings yet.";
        noDataMessage = "Save a column mapping from the import wizard to reuse it on future uploads.";
    }

    return (
        <ShowHide
            evaluator={canViewMappings}
            show={(
                <>
                    <div className="filter-controls">
                        <div className="inline-items spacer-bottom-small">
                            <div className="inline-item fill">
                                <div className="form-field-full">
                                    <FormTextInput
                                        inputRef={inputSearchRef}
                                        placeholder="Search by mapping name"
                                        onChange={onSearchChange}
                                        value={searchTerm}
                                        large
                                        disabled={isLoading && inputSearchRef.current !== document?.activeElement}
                                        icon="search"
                                        id="saved-mappings-search-field"
                                    />
                                </div>
                            </div>
                            <div className="inline-item">
                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                            </div>
                        </div>
                        <Filter
                            filterName={filterName}
                            visible={showFilters}
                            filters={pageFilters}
                            onUpdate={onFilterChange}
                            onClickOutside={onToggleFilter}
                        />
                    </div>

                    <NotificationInline
                        intent="warning"
                        show={sourceFailureText != null}
                        allowClose={false}
                        text={sourceFailureText}
                    />

                    <ListingTable
                        id="saved-mappings-table"
                        showPerPage={true}
                        pageSize={defaultPageSize}
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={sortedMappings.length}
                        loadingData={isLoading}
                        onPagingChange={onPagingChange}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onTableSort}
                        noDataHeading={noDataHeading}
                        noDataMessage={noDataMessage}
                    />

                    <AlertConfirm
                        title="Please confirm"
                        confirmButtonIntent="danger"
                        isOpen={showDeleteConfirm}
                        onConfirm={onDeleteConfirm}
                        onCancel={onDeleteCancel}
                    >
                        <p>Are you sure you want to delete the mapping '{mappingToDelete?.mappingName}'?</p>
                    </AlertConfirm>
                </>
            )}
        />
    );
}

export function SavedMappings({ breadcrumbs = defaultArray }) {
    return (
        <BulkImports breadcrumbs={breadcrumbs} selectedTabIndex={1}>
            <SavedMappingsPanel />
        </BulkImports>
    );
}

SavedMappings.propTypes = {
    breadcrumbs: PropTypes.array
};
